import React from 'react';

import {Button, ButtonProps} from '@yandex-data-ui/common';

import {compose} from 'recompose';
import withLocale, {WithLocaleProps} from 'hoc/withLocale';
import withEnv, {WithEnvProps} from 'hoc/withEnv';
import withIAM, {withIAMProps} from 'hoc/withIAM';
import withTranslation, {WithTranslationProps} from 'hoc/withTranslation';

import metrika from 'counters/metrika';
import pixel, {PixelEventType} from 'counters/pixel';
import {getConsoleLink, getConsoleSpecialLink, setUrlTld} from 'utils';

export interface LinkProps {
    url: string;
    params?: Record<string, string>;
}

export interface OwnProps extends Partial<ButtonProps> {
    text?: string;
    context?: string;
    id?: string;
    link?: LinkProps;
    exactLink?: string;
    path?: string;
    className?: string;
}

export type EnterConsoleLinkProps = OwnProps &
    WithEnvProps &
    WithLocaleProps &
    withIAMProps &
    WithTranslationProps;

class EnterConsoleLink extends React.Component<EnterConsoleLinkProps> {
    render() {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const {iam, text, context, id, locale, env, className, ...restProps} = this.props;

        return (
            <Button
                view="action"
                size="l"
                href={this.getLink()}
                onClick={this.handleClick}
                className={className}
                {...restProps}
            >
                {this.getText()}
            </Button>
        );
    }

    private getText = () => {
        const {iam, text, i18n} = this.props;

        if (text) {
            return text;
        }

        if (iam) {
            return i18n(
                'common',
                iam.hasClouds ? 'button_console-enter' : 'button_console-connect',
            );
        }

        return '';
    };

    private getLink = () => {
        const {
            exactLink,
            link,
            locale: {tld},
            env,
            path,
        } = this.props;

        if (exactLink) {
            return setUrlTld(exactLink, tld);
        }

        const domain = getConsoleLink(tld, env);

        if (link) {
            return getConsoleSpecialLink(tld, env, link.url, link.params);
        } else if (path) {
            return new URL(path, domain).href;
        }

        return domain;
    };

    private getContextPrefix = (context?: string, id?: string) => {
        switch (context) {
            case 'header':
                return 'TOP';
            case 'footer':
                return 'BOTTOM';
            case 'main':
                return 'MAIN';
            case 'service':
                return metrika.getServicePrefix(id);
            case 'marketplace':
                return metrika.getMarketPlacePrefix(id);
            default:
                return null;
        }
    };

    private handleClick = () => {
        const {context, id} = this.props;

        metrika.reachGoal('cross-site', metrika.goalGoToConsole('KEY'));
        metrika.reachGoal('main', metrika.goalGoToConsole('KEY'));

        const prefix = this.getContextPrefix(context, id);
        const goal = metrika.goalGoToConsole(prefix);

        if (goal) {
            metrika.reachGoal(context === 'marketplace' ? 'cross-site' : 'main', goal);
        }

        pixel.trackStandard(PixelEventType.Lead, {content_category: context, content_name: id});
    };
}

export default compose<EnterConsoleLinkProps, OwnProps>(
    withEnv,
    withLocale,
    withIAM,
    withTranslation(),
)(EnterConsoleLink);
